export interface PromptStorage {
   getItem(key: string): string | null;
   setItem(key: string, value: string): void;
   removeItem(key: string): void;
}

export interface PromptContext {
   routeName: string;
   isMobile: boolean;
   isAuthenticated: boolean;
   accountId: string | null;
   profileReady?: boolean;
   sessionId?: string | null;
}

export interface BookingPromptState {
   ready: boolean;
   welcomePending: boolean;
   emergencyPending: boolean;
}

export interface PromptSnapshot {
   showAppPrompt: boolean;
   showWelcome: boolean;
   appPromptPending: boolean;
   emergencyPending: boolean;
   // Something already owns the screen: the profile is not ready, the booking page
   // is still deciding which dialog comes first, or a named blocker is set.
   blocked: boolean;
   // The route this snapshot was computed for, so a reader can tell when it is
   // still describing the previous page.
   routeName: string;
}

// This identity is only a UI hint; authentication is still handled by the auth store and backend.
export function getClientPromptSessionId(accessToken?: string | null): string | null {
   if (!accessToken) return null;
   try {
      const parts = accessToken.split('.');
      if (parts.length !== 3) return null;
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const claims: unknown = JSON.parse(
         atob(payload.padEnd(Math.ceil(payload.length / 4) * 4, '=')),
      );
      if (!claims || typeof claims !== 'object') return null;
      const { role, user_id } = claims as { role?: unknown; user_id?: unknown };
      if (
         role !== 'client' ||
         typeof user_id !== 'number' ||
         !Number.isSafeInteger(user_id) ||
         user_id <= 0
      )
         return null;
      return `client:${user_id}`;
   } catch {
      return null;
   }
}

export const isClientAppPromptRoute = (name: string) => {
   if (
      [
         'auth-client-register-verify-email',
         'auth-client-register-risk-assessment',
         'auth-client-register-parent-verified',
      ].includes(name)
   )
      return false;
   return (
      ['client-booking', 'client-flaged', 'client-contact-admin'].includes(name) ||
      name.startsWith('auth-client-register-') ||
      ['therapist-recommendations', 'insurance-pending', 'insurance-failed'].includes(name)
   );
};

export function createClientMobileAppPromptController(storage?: PromptStorage) {
   const prefix = 'client-mobile-app-prompt:v2:';
   const memory = new Map<string, string | null>();
   const blockers = new Set<string>();
   const listeners = new Set<(snapshot: PromptSnapshot) => void>();
   let context: PromptContext = {
      routeName: '',
      isMobile: false,
      isAuthenticated: false,
      accountId: null,
   };
   let booking: BookingPromptState | null = null;
   let lastAccountId: string | null = null;
   let activePromptAccount: string | null = null;
   let snapshot: PromptSnapshot = {
      showAppPrompt: false,
      showWelcome: false,
      appPromptPending: false,
      emergencyPending: false,
      blocked: false,
      routeName: '',
   };

   // The in-memory copy also works when browser storage is unavailable or full.
   const read = (key: string) => {
      if (memory.has(key)) return memory.get(key) ?? null;
      try {
         const value = storage?.getItem(key) ?? null;
         memory.set(key, value);
         return value;
      } catch {
         return null;
      }
   };
   const write = (key: string, value: string | null) => {
      memory.set(key, value);
      try {
         if (value === null) storage?.removeItem(key);
         else storage?.setItem(key, value);
      } catch {
         // Navigation and dialog dismissal must work without session storage.
      }
   };
   const key = (accountId: string, purpose: string) => `${prefix}${accountId}:${purpose}`;

   const publish = (next: PromptSnapshot) => {
      if (
         Object.keys(next).every(
            (field) =>
               next[field as keyof PromptSnapshot] === snapshot[field as keyof PromptSnapshot],
         )
      )
         return;
      snapshot = next;
      listeners.forEach((listener) => listener({ ...snapshot }));
   };

   const evaluate = () => {
      const accountId = context.accountId;
      if (
         context.isAuthenticated &&
         context.profileReady !== false &&
         accountId &&
         context.sessionId &&
         read(key(context.sessionId, 'signup-awaiting-profile')) === '1'
      ) {
         if (read(key(accountId, 'signup')) !== '1') write(key(accountId, 'signup-pending'), '1');
         write(key(context.sessionId, 'signup-awaiting-profile'), null);
      }
      const eligibleRoute =
         context.isMobile && context.isAuthenticated && isClientAppPromptRoute(context.routeName);
      const eligible = eligibleRoute && context.profileReady !== false && !!accountId;
      const onBooking = context.routeName === 'client-booking';
      const emergencyPending = onBooking && !!booking?.emergencyPending;
      if (!eligibleRoute || (accountId && activePromptAccount !== accountId))
         activePromptAccount = null;

      const signupPending =
         !!accountId &&
         onBooking &&
         read(key(accountId, 'signup-pending')) === '1' &&
         read(key(accountId, 'signup')) !== '1';
      const sessionPending = !!accountId && read(key(accountId, 'session')) !== '1';
      const appPromptPending =
         eligibleRoute && (signupPending || sessionPending || !!activePromptAccount);
      // Booking publishes its refreshed profile and emergency requirement before any optional dialog opens.
      const ready = !onBooking || !!booking?.ready;
      const blocked =
         context.profileReady === false || !ready || emergencyPending || blockers.size > 0;

      if (appPromptPending && !blocked && accountId) {
         activePromptAccount = accountId;
         write(key(accountId, 'session'), '1');
         if (signupPending) {
            write(key(accountId, 'signup'), '1');
            write(key(accountId, 'signup-pending'), null);
         }
      }

      const showAppPrompt = eligible && !!activePromptAccount && !blocked;
      const showWelcome =
         context.isAuthenticated &&
         !!accountId &&
         onBooking &&
         ready &&
         !!booking?.welcomePending &&
         !blocked &&
         !appPromptPending;
      publish({
         showAppPrompt,
         showWelcome,
         appPromptPending,
         emergencyPending,
         blocked,
         routeName: context.routeName,
      });
   };

   return {
      sync(next: PromptContext) {
         if (
            (context.routeName === 'client-booking' && next.routeName !== 'client-booking') ||
            !next.isAuthenticated
         )
            booking = null;
         if (booking && lastAccountId && next.accountId && lastAccountId !== next.accountId) {
            booking.welcomePending = false;
         }
         if (!next.isAuthenticated) lastAccountId = null;
         else if (next.accountId) lastAccountId = next.accountId;
         context = next;
         evaluate();
      },
      setBooking(next: BookingPromptState) {
         booking = { ...next };
         evaluate();
      },
      clearBooking() {
         booking = null;
         evaluate();
      },
      setBlocker(name: string, blocked: boolean) {
         if (blocked) blockers.add(name);
         else blockers.delete(name);
         evaluate();
      },
      markSignupPending(accountId: string) {
         if (accountId && read(key(accountId, 'signup')) !== '1')
            write(key(accountId, 'signup-pending'), '1');
         evaluate();
      },
      markSignupAwaitingProfile(sessionId: string) {
         if (sessionId) write(key(sessionId, 'signup-awaiting-profile'), '1');
         evaluate();
      },
      closeAppPrompt() {
         activePromptAccount = null;
         evaluate();
      },
      closeWelcome() {
         if (booking) booking.welcomePending = false;
         evaluate();
      },
      getSnapshot: () => ({ ...snapshot }),
      subscribe(listener: (state: PromptSnapshot) => void) {
         listeners.add(listener);
         listener({ ...snapshot });
         return () => {
            listeners.delete(listener);
         };
      },
   };
}
